import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X,
  Sparkles,
  MapPin,
  Ruler,
  ArrowRight,
  Loader2,
  Zap,
  Clock,
  Mail,
  User,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLAN_TYPES = [
  { value: "2D Floor Plan", label: "2D Floor Plan" },
  { value: "3D Floor Plan", label: "3D Floor Plan" },
  { value: "Renew Existing Floor Plan", label: "Renew Existing Plan" },
  { value: "Other", label: "Other" },
];

const STYLE_OPTIONS = [
  "Black & White",
  "Colorized",
  "Black & White + Colorized",
];

const URGENCY_OPTIONS = [
  { value: "within_48h", label: "Within 48 hours", icon: Zap, tint: "amber" },
  { value: "within_1w", label: "Within 1 week", icon: Clock, tint: "teal" },
];

export default function QuickRequestModal({
  open,
  onClose,
  prefill = {},
  authHeader,
  onSuccess,
}) {
  const [address, setAddress] = useState("");
  const [sqft, setSqft] = useState("");
  const [planType, setPlanType] = useState(PLAN_TYPES[0].value);
  const [style, setStyle] = useState(STYLE_OPTIONS[0]);
  const [urgency, setUrgency] = useState(URGENCY_OPTIONS[0].value);
  const [comments, setComments] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const hasPrefillIdentity = Boolean(prefill?.email);

  // Reset on open — honor prefill.planType when supplied
  useEffect(() => {
    if (open) {
      setAddress("");
      setSqft("");
      setPlanType(prefill?.planType || PLAN_TYPES[0].value);
      setStyle(STYLE_OPTIONS[0]);
      setUrgency(URGENCY_OPTIONS[0].value);
      setComments("");
      setName(prefill?.name || "");
      setEmail(prefill?.email || "");
    }
  }, [open, prefill?.planType, prefill?.name, prefill?.email]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!address.trim()) {
      toast.error("Please add the property address.");
      return;
    }
    const submitName = (name || prefill?.name || "").trim();
    const submitEmail = (email || prefill?.email || "").trim();
    if (!submitName) {
      toast.error("Please add your name.");
      return;
    }
    if (!submitEmail) {
      toast.error("Please add your email.");
      return;
    }
    setSubmitting(true);
    const stylesAllowed = planType === "2D Floor Plan" || planType === "3D Floor Plan";
    const trimmedComments = comments.trim();
    const prefix = (prefill?.messagePrefix || "").trim();
    const composedMessage = [prefix ? `[${prefix}]` : "", trimmedComments]
      .filter(Boolean)
      .join(" — ")
      .trim();
    try {
      const payload = {
        name: submitName,
        email: submitEmail,
        property_address: address.trim(),
        estimated_sqft: sqft ? parseInt(sqft, 10) : null,
        service_type: planType,
        style: stylesAllowed ? style : null,
        urgency,
        message: composedMessage || null,
      };
      const { data } = await axios.post(`${API}/leads`, payload, {
        headers: authHeader ? authHeader() : {},
        withCredentials: true,
      });
      toast.success("Request submitted — we'll be in touch within 24 hours.");
      onSuccess?.(data);
      onClose?.();
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      // FastAPI validation errors come back as an array of {loc, msg, ...}.
      // Surface the first field-level message so users know what to fix.
      let msg = "Could not submit. Please try again.";
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail) && detail[0]?.msg) {
        const field = Array.isArray(detail[0].loc)
          ? detail[0].loc[detail[0].loc.length - 1]
          : "field";
        msg = `Please check the ${field} field — ${detail[0].msg}`;
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center px-4 py-8 sm:py-10 bg-black/45 backdrop-blur-sm overflow-y-auto"
      data-testid="quick-request-modal"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="w-full max-w-md bg-white rounded-[2rem] shadow-2xl border border-stone-200 overflow-hidden my-auto">
        <div className="flex items-start justify-between px-6 pt-6 pb-2">
          <div>
            <span className="inline-flex items-center gap-2 bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider mb-2">
              <Sparkles className="h-3 w-3" />
              New request
            </span>
            <h2 className="font-display text-2xl font-semibold tracking-tight text-stone-900">
              Quick request
            </h2>
            <p className="text-sm text-stone-600 mt-1">
              Three details. We&apos;ll reach out within one business day.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="quick-request-close"
            className="h-9 w-9 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center flex-shrink-0 ml-3"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="px-6 pt-4 pb-6 space-y-4" data-testid="quick-request-form">
          {!hasPrefillIdentity && (
            <>
              <Field label="Your Name" required>
                <IconInput
                  icon={User}
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Smith"
                  autoComplete="name"
                  data-testid="quick-request-name"
                  required
                />
              </Field>

              <Field label="Your Email" required>
                <IconInput
                  icon={Mail}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  data-testid="quick-request-email"
                  required
                />
              </Field>
            </>
          )}

          <Field label="Property Address" required>
            <IconInput
              icon={MapPin}
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="123 K St NW, Washington, DC"
              data-testid="quick-request-address"
              required
            />
          </Field>

          <Field label="Estimated Sq Ft">
            <IconInput
              icon={Ruler}
              type="number"
              inputMode="numeric"
              min="0"
              value={sqft}
              onChange={(e) => setSqft(e.target.value)}
              placeholder="e.g. 1850"
              data-testid="quick-request-sqft"
            />
          </Field>

          <Field label="Floor Plan Type" required>
            <select
              data-testid="quick-request-type"
              value={planType}
              onChange={(e) => setPlanType(e.target.value)}
              className="w-full bg-[#FAF7F2] border border-stone-200 rounded-full px-4 py-3 text-sm text-stone-900 focus:outline-none focus:border-teal-700 focus:bg-white focus:ring-4 focus:ring-teal-700/10 transition-colors cursor-pointer"
            >
              {PLAN_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </Field>

          {(planType === "2D Floor Plan" || planType === "3D Floor Plan") && (
            <Field label="Style" required>
              <select
                data-testid="quick-request-style"
                value={style}
                onChange={(e) => setStyle(e.target.value)}
                className="w-full bg-[#FAF7F2] border border-stone-200 rounded-full px-4 py-3 text-sm text-stone-900 focus:outline-none focus:border-teal-700 focus:bg-white focus:ring-4 focus:ring-teal-700/10 transition-colors cursor-pointer"
              >
                {STYLE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {planType === "Renew Existing Floor Plan" && (
            <div
              className="bg-rose-50 border border-rose-200 rounded-2xl p-4"
              data-testid="quick-request-renew-banner"
            >
              <div className="flex items-start gap-3">
                <div className="inline-flex h-9 w-9 rounded-xl bg-rose-100 items-center justify-center flex-shrink-0">
                  <Mail className="h-4 w-4 text-rose-700" />
                </div>
                <div className="text-sm text-stone-800 leading-relaxed">
                  <div className="font-semibold text-stone-900">
                    Upload renewable plans to{" "}
                    <a
                      href={`mailto:hello@dmvfp.com?subject=${encodeURIComponent(
                        "Renew Existing Floor Plan request"
                      )}&body=${encodeURIComponent(
                        `Hi DMV Floor Plans team,\n\nI'd like to renew an existing floor plan.\n\nProperty address: ${
                          address || "(add address)"
                        }\nEstimated sq ft: ${
                          sqft || "(add sqft)"
                        }\n\nMy existing plans are attached.\n\nThanks!`
                      )}`}
                      data-testid="renew-email-link"
                      className="text-rose-700 underline font-bold hover:text-rose-900"
                    >
                      hello@dmvfp.com
                    </a>
                  </div>
                  <div className="text-xs text-stone-600 mt-1">
                    Tap the email to open your mail app with a pre-filled
                    message — attach your existing PDF, image or DXF files and
                    hit send. We&apos;ll match it to this request.
                  </div>
                </div>
              </div>
            </div>
          )}

          <Field label="How soon do you need it?" required>
            <div className="grid grid-cols-2 gap-2" data-testid="quick-request-urgency">
              {URGENCY_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const isActive = urgency === opt.value;
                const activeCls =
                  opt.tint === "amber"
                    ? "bg-amber-600 text-white border-amber-600"
                    : "bg-teal-700 text-white border-teal-700";
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setUrgency(opt.value)}
                    data-testid={`quick-request-urgency-${opt.value}`}
                    className={`inline-flex items-center justify-center gap-2 border-2 rounded-2xl px-3 py-3 text-sm font-semibold transition-colors ${
                      isActive
                        ? activeCls
                        : "bg-white text-stone-800 border-stone-200 hover:border-stone-400"
                    }`}
                  >
                    <Icon className="h-4 w-4" /> {opt.label}
                  </button>
                );
              })}
            </div>
            <p
              className="mt-2 text-[11px] italic text-stone-500 pl-1"
              style={{
                display: (prefill?.messagePrefix || "").toLowerCase().includes("rlah") ? "none" : "block",
              }}
            >
              *requests needed within 48 hours of contact occur a 50% rush fee*
            </p>
          </Field>

          <Field label="Comments (optional)">
            <textarea
              data-testid="quick-request-comments"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={3}
              placeholder="Anything else we should know — access, deadlines, special requests…"
              className="w-full bg-[#FAF7F2] border border-stone-200 rounded-2xl px-4 py-3 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-teal-700 focus:bg-white focus:ring-4 focus:ring-teal-700/10 transition-colors resize-none"
            />
          </Field>

          <button
            type="submit"
            disabled={submitting}
            data-testid="quick-request-submit"
            className="group w-full inline-flex items-center justify-center gap-3 bg-stone-900 text-white py-3.5 rounded-full text-sm font-semibold hover:bg-teal-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
              </>
            ) : (
              <>
                Submit request
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </>
            )}
          </button>

          <p className="text-xs text-stone-500 text-center">
            Saved to your account history.
          </p>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-stone-600 mb-1.5 block pl-1">
        {label} {required && <span className="text-teal-700">*</span>}
      </span>
      {children}
    </label>
  );
}

function IconInput({ icon: Icon, ...rest }) {
  return (
    <span className="relative block">
      <span className="absolute inset-y-0 left-3 flex items-center text-stone-400">
        <Icon className="h-4 w-4" />
      </span>
      <input
        {...rest}
        className="w-full bg-[#FAF7F2] border border-stone-200 rounded-full pl-10 pr-4 py-3 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-teal-700 focus:bg-white focus:ring-4 focus:ring-teal-700/10 transition-colors"
      />
    </span>
  );
}
